/**
 * Gate artifact body ↔ editable peer list for twin-test authoring.
 *
 * Owns parse / serialize / resolve-payload for the questions JSON at the gate.
 * Field patches in `gateQuestionEdits` and the RunGateQuestionList controls go
 * through this adapter so type-switch memory and wire shape stay one place.
 *
 * Wave 3: eight quick-study types including matrix grids. Mixed matrix_rating
 * scales = per-column min/max with top-level `scale` null.
 *
 * Cache scope: one-shot, not cached (local JSON surgery, no LLM).
 */

import {
  QUESTION_TYPE_REGISTRY,
  RATING_UNIT_REGISTRY,
  optionPlaceholderLabel,
  questionTypeIdsForMode,
} from '@digital-twins/shared-types'

import type { ParseKeys } from 'i18next'
import i18n from '@/i18n'

const FENCED_BLOCK = /(```[a-zA-Z]*\s*\n)([\s\S]*?)(```)/

/** Peer types twin-test can run (quick_study registry, including matrices). */
export const TWIN_TEST_GATE_QUESTION_TYPES: ReadonlyArray<string> =
  questionTypeIdsForMode('quick_study')

export interface TwinTestGateRatingSpan {
  min: number
  max: number
  label: string
  unitId: string
}

/** Rating spans the seam accepts, derived from `RATING_UNIT_REGISTRY`. */
export const TWIN_TEST_GATE_RATING_SPANS: ReadonlyArray<TwinTestGateRatingSpan> =
  Object.values(RATING_UNIT_REGISTRY)
    .filter(
      (unit) =>
        typeof unit.rawMin === 'number' && typeof unit.rawMax === 'number',
    )
    .map((unit) => ({
      unitId: unit.id,
      min: unit.rawMin as number,
      max: unit.rawMax as number,
      label: unit.label,
    }))

export const TWIN_TEST_GATE_DEFAULT_RATING_SPAN: TwinTestGateRatingSpan =
  TWIN_TEST_GATE_RATING_SPANS.find((span) => span.unitId === 'rating_1_5') ??
  TWIN_TEST_GATE_RATING_SPANS[0]

export interface GateEditableColumn {
  id: string
  label: string
  /** Rating span; null on categorical columns (labels only). */
  min: number | null
  max: number | null
  /**
   * Optional endpoint anchors. Independent ends — one-sided is legitimate
   * (unlike min/max). Null when absent/blank; never invent an anchor.
   */
  minLabel: string | null
  maxLabel: string | null
}

export interface GateEditableRow {
  id: string
  label: string
}

export interface GateEditableQuestion {
  questionType: string
  title: string
  prompt: string
  scale: {
    min: number
    max: number
    minLabel: string
    maxLabel: string
  } | null
  choices: Array<string>
  columns: Array<GateEditableColumn> | null
  rows: Array<GateEditableRow> | null
  versions: Array<{ id: string; label: string; content: string }> | null
}

/** Fields stashed per prior type when the reviewer switches types. */
export interface GateTypeStash {
  scale: GateEditableQuestion['scale']
  choices: Array<string>
  columns: Array<GateEditableColumn> | null
  rows: Array<GateEditableRow> | null
}

export type GateTypeMemory = Map<
  number,
  Partial<Record<string, GateTypeStash>>
>

export interface TwinTestGateQuestionAdapter {
  /**
   * Valid questions payload → editable list (length may be 0).
   * Not a questions artifact / malformed → `null` (distinct from empty).
   */
  parse(body: string): Array<GateEditableQuestion> | null
  serialize(questions: Array<GateEditableQuestion>, priorBody: string): string
  toResolvePayload(questions: Array<GateEditableQuestion>): unknown
}

interface TypedQuestionsBody {
  value: unknown
  entries: Array<Record<string, unknown>>
}

function splitFencedBody(body: string): {
  open: string
  inner: string
  close: string
} {
  const fenced = FENCED_BLOCK.exec(body)
  if (!fenced) {
    return { open: '', inner: body, close: '' }
  }

  return {
    open: body.slice(0, fenced.index) + fenced[1],
    inner: fenced[2],
    close: fenced[3] + body.slice(fenced.index + fenced[0].length),
  }
}

function serializeTypedBody(value: unknown, body: string): string {
  const parts = splitFencedBody(body)
  const json = JSON.stringify(value, null, parts.inner.includes('\n') ? 2 : 0)
  return parts.open ? `${parts.open}${json}\n${parts.close}` : json
}

function readString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * Typed questions payload, including a valid empty list.
 * Null when the body is not a questions artifact.
 */
export function readTwinTestGateTypedBody(
  body: string,
): TypedQuestionsBody | null {
  const source = splitFencedBody(body).inner.trim()
  if (!source.startsWith('{') && !source.startsWith('[')) {
    return null
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(source)
  } catch {
    return null
  }

  let raw: unknown
  if (Array.isArray(parsed)) {
    raw = parsed
  } else if (parsed && typeof parsed === 'object') {
    raw = (parsed as Record<string, unknown>).questions
  } else {
    return null
  }

  if (!Array.isArray(raw)) {
    return null
  }

  const entries: Array<Record<string, unknown>> = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return null
    }
    entries.push(entry as Record<string, unknown>)
  }

  return { value: parsed, entries }
}

function readScale(
  value: unknown,
): GateEditableQuestion['scale'] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }
  const candidate = value as Record<string, unknown>
  const min = candidate.min
  const max = candidate.max
  if (typeof min !== 'number' || typeof max !== 'number') {
    return null
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null
  }
  return {
    min,
    max,
    minLabel:
      readString(candidate.minLabel) ??
      readString(candidate.min_label) ??
      '',
    maxLabel:
      readString(candidate.maxLabel) ??
      readString(candidate.max_label) ??
      '',
  }
}

function readChoiceList(value: unknown): Array<string> {
  if (!Array.isArray(value)) {
    return []
  }
  return value.map((entry) => (typeof entry === 'string' ? entry : ''))
}

function columnId(index: number): string {
  return index < 26 ? `c${String.fromCharCode(97 + index)}` : `col_${index + 1}`
}

function rowId(index: number): string {
  return index < 26 ? `r${String.fromCharCode(97 + index)}` : `row_${index + 1}`
}

function readColumns(value: unknown): Array<GateEditableColumn> | null {
  if (!Array.isArray(value) || value.length === 0) {
    return null
  }
  const columns: Array<GateEditableColumn> = []
  for (let index = 0; index < value.length; index += 1) {
    const entry = value[index]
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return null
    }
    const candidate = entry as Record<string, unknown>
    const label =
      readString(candidate.label) ??
      readString(candidate.title) ??
      `Measure ${index + 1}`
    const id = readString(candidate.id) ?? columnId(index)
    const hasMin =
      typeof candidate.min === 'number' && Number.isFinite(candidate.min)
    const hasMax =
      typeof candidate.max === 'number' && Number.isFinite(candidate.max)
    if (hasMin !== hasMax) {
      return null
    }
    columns.push({
      id,
      label,
      min: hasMin ? (candidate.min as number) : null,
      max: hasMax ? (candidate.max as number) : null,
      minLabel:
        readString(candidate.minLabel) ??
        readString(candidate.min_label) ??
        null,
      maxLabel:
        readString(candidate.maxLabel) ??
        readString(candidate.max_label) ??
        null,
    })
  }
  return columns
}

function readRows(value: unknown): Array<GateEditableRow> | null {
  if (!Array.isArray(value) || value.length === 0) {
    return null
  }
  const rows: Array<GateEditableRow> = []
  for (let index = 0; index < value.length; index += 1) {
    const entry = value[index]
    if (typeof entry === 'string') {
      const label = entry.trim()
      if (!label) {
        return null
      }
      rows.push({ id: rowId(index), label })
      continue
    }
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return null
    }
    const candidate = entry as Record<string, unknown>
    const label =
      readString(candidate.label) ??
      readString(candidate.title) ??
      `Row ${index + 1}`
    const id = readString(candidate.id) ?? rowId(index)
    rows.push({ id, label })
  }
  return rows
}

function readVersionList(
  value: unknown,
): GateEditableQuestion['versions'] {
  if (!Array.isArray(value) || value.length < 2) {
    return null
  }

  const versions: Array<{ id: string; label: string; content: string }> = []
  for (let index = 0; index < value.length; index += 1) {
    const entry = value[index]
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return null
    }
    const candidate = entry as Record<string, unknown>
    const label = readString(candidate.label)
    const content = readString(candidate.content)
    if (!label || !content) {
      return null
    }
    const id =
      readString(candidate.id) ??
      (index < 26
        ? String.fromCharCode(97 + index)
        : `ver_${index + 1}`)
    versions.push({ id, label, content })
  }
  return versions
}

function entryToEditable(entry: Record<string, unknown>): GateEditableQuestion {
  const questionType =
    readString(entry.questionType) ??
    readString(entry.question_type) ??
    'open_end'
  const choices = readChoiceList(entry.choices ?? entry.options)
  const needsColumns = typeNeedsColumns(questionType)
  return {
    questionType,
    title: typeof entry.title === 'string' ? entry.title : '',
    prompt: typeof entry.prompt === 'string' ? entry.prompt : '',
    scale: readScale(entry.scale),
    choices: needsColumns ? [] : choices,
    columns: needsColumns ? readColumns(entry.columns) : null,
    rows: needsColumns ? readRows(entry.rows) : null,
    versions: readVersionList(entry.versions),
  }
}

function typeNeedsChoices(questionType: string): boolean {
  const entry = QUESTION_TYPE_REGISTRY[
    questionType as keyof typeof QUESTION_TYPE_REGISTRY
  ]
  return Boolean(entry?.content.requiresChoices && !entry?.content.requiresColumns)
}

function typeNeedsScale(questionType: string): boolean {
  const entry = QUESTION_TYPE_REGISTRY[
    questionType as keyof typeof QUESTION_TYPE_REGISTRY
  ]
  // Grids may use per-column spans instead of a shared top-level scale.
  return Boolean(entry?.content.requiresScale && !entry?.content.requiresColumns)
}

export function typeNeedsColumns(questionType: string): boolean {
  const entry = QUESTION_TYPE_REGISTRY[
    questionType as keyof typeof QUESTION_TYPE_REGISTRY
  ]
  return Boolean(entry?.content.requiresColumns)
}

function defaultColumns(questionType: string): Array<GateEditableColumn> {
  const span = TWIN_TEST_GATE_DEFAULT_RATING_SPAN
  if (questionType === 'matrix_categorical') {
    return [
      {
        id: 'ca',
        label: 'Yes',
        min: null,
        max: null,
        minLabel: null,
        maxLabel: null,
      },
      {
        id: 'cb',
        label: 'No',
        min: null,
        max: null,
        minLabel: null,
        maxLabel: null,
      },
    ]
  }
  return [
    {
      id: 'ca',
      label: 'Appeal',
      min: span.min,
      max: span.max,
      minLabel: null,
      maxLabel: null,
    },
    {
      id: 'cb',
      label: 'Clarity',
      min: span.min,
      max: span.max,
      minLabel: null,
      maxLabel: null,
    },
  ]
}

/**
 * Copy shared top-level scale anchors onto columns that do not already have
 * their own, then null the shared scale. Used at every site that is about to
 * drop `scale` so authored anchors are not lost. Does not invent labels when
 * the shared scale had none.
 */
export function migrateSharedScaleAnchorsToColumns(
  question: GateEditableQuestion,
): GateEditableQuestion {
  const columns = question.columns
  if (!columns || columns.length === 0) {
    return { ...question, scale: null }
  }

  const sharedMin = question.scale
    ? readString(question.scale.minLabel)
    : null
  const sharedMax = question.scale
    ? readString(question.scale.maxLabel)
    : null

  if (!sharedMin && !sharedMax) {
    return { ...question, scale: null }
  }

  const nextColumns = columns.map((column) => ({
    ...column,
    minLabel: readString(column.minLabel) ?? sharedMin,
    maxLabel: readString(column.maxLabel) ?? sharedMax,
  }))
  return { ...question, columns: nextColumns, scale: null }
}

function defaultRows(): Array<GateEditableRow> {
  return [
    { id: 'ra', label: 'Row 1' },
    { id: 'rb', label: 'Row 2' },
  ]
}

/**
 * True when every column shares the same min/max — then a top-level scale may
 * be kept. Mixed spans force top-level scale null.
 */
export function columnsHaveUniformSpan(
  columns: Array<GateEditableColumn> | null,
): boolean {
  if (!columns || columns.length === 0) {
    return false
  }
  const first = columns[0]
  if (typeof first.min !== 'number' || typeof first.max !== 'number') {
    return false
  }
  return columns.every(
    (column) => column.min === first.min && column.max === first.max,
  )
}

function gateChoiceLabel(index: number, language?: string | null): string {
  return optionPlaceholderLabel(language ?? undefined, index)
}

/** Studies-like empty defaults for a blank / type-switched peer. */
export function blankGateEditableQuestion(
  questionType: string,
  language?: string | null,
): GateEditableQuestion {
  const type = TWIN_TEST_GATE_QUESTION_TYPES.includes(questionType)
    ? questionType
    : 'open_end'

  const base: GateEditableQuestion = {
    questionType: type,
    title: '',
    prompt: '',
    scale: null,
    choices: [],
    columns: null,
    rows: null,
    versions: null,
  }

  if (typeNeedsScale(type)) {
    const span = TWIN_TEST_GATE_DEFAULT_RATING_SPAN
    base.scale = {
      min: span.min,
      max: span.max,
      minLabel: '',
      maxLabel: '',
    }
  }

  if (typeNeedsChoices(type)) {
    const count = type === 'ranking' ? 5 : 2
    base.choices = Array.from({ length: count }, (_unused, offset) =>
      gateChoiceLabel(offset + 1, language),
    )
  }

  if (typeNeedsColumns(type)) {
    base.columns = defaultColumns(type)
    base.rows = defaultRows()
    // Mixed-scale ready: per-column min/max, no shared top-level scale.
    base.scale = null
  }

  return base
}

export function stashGateTypeFields(
  question: GateEditableQuestion,
): GateTypeStash {
  return {
    scale: question.scale
      ? {
          min: question.scale.min,
          max: question.scale.max,
          minLabel: question.scale.minLabel,
          maxLabel: question.scale.maxLabel,
        }
      : null,
    choices: [...question.choices],
    columns: question.columns
      ? question.columns.map((column) => ({ ...column }))
      : null,
    rows: question.rows ? question.rows.map((row) => ({ ...row })) : null,
  }
}

function filledChoices(choices: Array<string>): Array<string> {
  return choices.filter((choice) => choice.trim().length > 0)
}

/**
 * Type-specific stash wins; otherwise carry the current option list when both
 * types need choices (single_select ↔ ranking ↔ multi_select).
 */
function resolveTypeSwitchChoices(
  remembered: Array<string> | undefined,
  current: Array<string>,
  fallback: Array<string>,
): Array<string> {
  if (remembered && remembered.length >= 2) {
    return [...remembered]
  }
  const carried = filledChoices(current)
  if (carried.length >= 2) {
    return carried
  }
  return fallback
}

/**
 * Switch type with studies-like response memory: stash the current type's
 * fields, restore the target type's stash when present, else carry compatible
 * choice lists, else defaults. Title, prompt, and versions always carry.
 */
export function applyGateQuestionTypeSwitch(
  question: GateEditableQuestion,
  nextType: string,
  memoryForIndex: Partial<Record<string, GateTypeStash>> | undefined,
  language?: string | null,
): {
  question: GateEditableQuestion
  memory: Partial<Record<string, GateTypeStash>>
} {
  const type = TWIN_TEST_GATE_QUESTION_TYPES.includes(nextType)
    ? nextType
    : 'open_end'
  const memory = { ...(memoryForIndex ?? {}) }
  memory[question.questionType] = stashGateTypeFields(question)

  const remembered = memory[type]
  const blank = blankGateEditableQuestion(type, language)
  const next: GateEditableQuestion = {
    ...blank,
    title: question.title,
    prompt: question.prompt,
    versions: question.versions,
  }

  if (typeNeedsScale(type)) {
    next.scale = remembered?.scale ?? blank.scale
  }
  if (typeNeedsChoices(type)) {
    next.choices = resolveTypeSwitchChoices(
      remembered?.choices,
      question.choices,
      blank.choices,
    )
  }
  if (typeNeedsColumns(type)) {
    next.columns =
      remembered?.columns && remembered.columns.length > 0
        ? remembered.columns.map((column) => ({ ...column }))
        : blank.columns
    next.rows =
      remembered?.rows && remembered.rows.length > 0
        ? remembered.rows.map((row) => ({ ...row }))
        : blank.rows
    const migrated = migrateSharedScaleAnchorsToColumns({
      ...next,
      scale: question.scale,
      columns: next.columns,
    })
    next.columns = migrated.columns
    next.scale = migrated.scale
  }

  return { question: next, memory }
}

/** Reindex type-switch memory after a peer is removed. */
export function reindexGateTypeMemory(
  memory: GateTypeMemory,
  removedIndex: number,
): GateTypeMemory {
  const next: GateTypeMemory = new Map()
  for (const [index, stash] of memory.entries()) {
    if (index < removedIndex) {
      next.set(index, stash)
    } else if (index > removedIndex) {
      next.set(index - 1, stash)
    }
  }
  return next
}

function toWireEntry(question: GateEditableQuestion): Record<string, unknown> {
  const entry: Record<string, unknown> = {
    questionType: question.questionType,
    title: question.title,
    prompt: question.prompt,
  }

  if (typeNeedsScale(question.questionType) && question.scale) {
    entry.scale = {
      min: question.scale.min,
      max: question.scale.max,
      minLabel: question.scale.minLabel,
      maxLabel: question.scale.maxLabel,
    }
  }

  if (typeNeedsChoices(question.questionType)) {
    entry.choices = [...question.choices]
  }

  if (typeNeedsColumns(question.questionType)) {
    if (question.columns && question.columns.length > 0) {
      entry.columns = question.columns.map((column) => {
        const wire: Record<string, unknown> = {
          id: column.id,
          label: column.label,
        }
        if (typeof column.min === 'number' && typeof column.max === 'number') {
          wire.min = column.min
          wire.max = column.max
        }
        const minLabel = readString(column.minLabel)
        const maxLabel = readString(column.maxLabel)
        if (minLabel) {
          wire.minLabel = minLabel
        }
        if (maxLabel) {
          wire.maxLabel = maxLabel
        }
        return wire
      })
    }
    if (question.rows && question.rows.length > 0) {
      entry.rows = question.rows.map((row) => ({
        id: row.id,
        label: row.label,
      }))
    }
    // Shared scale only when every column shares the same span AND the reviewer
    // kept a top-level scale. Mixed columns → scale omitted (null on wire).
    if (
      question.scale &&
      columnsHaveUniformSpan(question.columns)
    ) {
      entry.scale = {
        min: question.scale.min,
        max: question.scale.max,
        minLabel: question.scale.minLabel,
        maxLabel: question.scale.maxLabel,
      }
    }
  }

  if (question.versions && question.versions.length >= 2) {
    entry.versions = question.versions.map((version) => ({
      id: version.id,
      label: version.label,
      content: version.content,
    }))
  }

  // No followUpStem / pairedOe.
  return entry
}

export const twinTestGateQuestionAdapter: TwinTestGateQuestionAdapter = {
  parse(body: string): Array<GateEditableQuestion> | null {
    const typed = readTwinTestGateTypedBody(body)
    if (!typed) {
      return null
    }
    return typed.entries.map(entryToEditable)
  },

  serialize(
    questions: Array<GateEditableQuestion>,
    priorBody: string,
  ): string {
    const typed = readTwinTestGateTypedBody(priorBody)
    if (!typed) {
      return priorBody
    }

    const wire = questions.map(toWireEntry)
    if (Array.isArray(typed.value)) {
      return serializeTypedBody(wire, priorBody)
    }

    const next = {
      ...(typed.value as Record<string, unknown>),
      questions: wire,
    }
    return serializeTypedBody(next, priorBody)
  },

  toResolvePayload(questions: Array<GateEditableQuestion>): unknown {
    return { questions: questions.map(toWireEntry) }
  },
}

export const GATE_QUESTION_TYPE_LABEL_KEYS: Readonly<Record<string, ParseKeys<'app'>>> = {
  rating: 'threads.detail.gate.types.rating',
  single_select: 'threads.detail.gate.types.single_select',
  multi_select: 'threads.detail.gate.types.multi_select',
  ranking: 'threads.detail.gate.types.ranking',
  continuous: 'threads.detail.gate.types.continuous',
  open_end: 'threads.detail.gate.types.open_end',
  matrix_rating: 'threads.detail.gate.types.matrix_rating',
  matrix_categorical: 'threads.detail.gate.types.matrix_categorical',
}

export function gateQuestionTypeLabel(questionType: string): string {
  const key = GATE_QUESTION_TYPE_LABEL_KEYS[questionType]
  return key ? i18n.t(key, { ns: 'app' }) : questionType
}
